import useSWR from "swr";
import { endpoints } from "../../../../endpoints";
import type { TdsGeneratedAssetSchema } from "../../../../types/types.PIM";
import { useStoreState } from "../../../../util/util";

function toGeneratedAssetsList(
  generatedAssets?: TdsGeneratedAssetSchema[] | unknown
): TdsGeneratedAssetSchema[] {
  if (Array.isArray(generatedAssets)) {
    return generatedAssets;
  }

  // Some responses/proxies wrap list payloads as `{ data: [...] }`.
  if (
    generatedAssets &&
    typeof generatedAssets === "object" &&
    Array.isArray((generatedAssets as { data?: unknown }).data)
  ) {
    return (generatedAssets as { data: TdsGeneratedAssetSchema[] }).data;
  }

  return [];
}

export function useTemplateGeneratedAssets(templateId?: string) {
  const { tenant_id } = useStoreState();
  const key =
    tenant_id && templateId
      ? endpoints.v2_tenants_tenant_id_templates_template_id_generated_assets(
          tenant_id,
          templateId
        )
      : null;

  const result = useSWR<TdsGeneratedAssetSchema[]>(key, {
    revalidateOnFocus: false,
    revalidateOnMount: true,
  });

  // SWR 0.3 serializes `null` keys to `""`, so disabled hooks can pick up
  // unrelated cache entries. Only expose data when the request was enabled.
  if (!key) {
    return { ...result, data: undefined, error: undefined };
  }

  return result;
}

export function getActiveTemplateGeneratedAssets(
  generatedAssets?: TdsGeneratedAssetSchema[] | unknown
): TdsGeneratedAssetSchema[] {
  return toGeneratedAssetsList(generatedAssets).filter(
    (asset) => asset.is_active
  );
}

export function getActiveGeneratedAssetForCategory(
  generatedAssets: TdsGeneratedAssetSchema[] | unknown,
  categoryId?: string
): TdsGeneratedAssetSchema | undefined {
  if (!categoryId) return undefined;

  const found = toGeneratedAssetsList(generatedAssets).find(
    (genAsset) => genAsset.category_id === categoryId
  );

  return found?.is_active ? found : undefined;
}
